import moment from "moment";
import { Card } from "shared/components/Card";
import {
  ApplicationStatus,
  DISCLOSURES,
  Invitee,
  VerificationNames,
  ApplicantType,
  DataVendors
} from "shared/types";
import { Cell } from "./components/Cell";
import { ApplicantOrInvitee, FullApplicant, Verification } from "./types";
import { ApplicantHeaderCell } from "./components/ApplicantHeaderCell";
import { Applicant } from "types/application";
import { VerificationBadge } from "./components/VerificationBadgeCell";
import { Fragment, useMemo, useState } from "react";
import { WithIcon } from "./components/WithIcon";
import { VERIFICATION_ICON, VERIFICATIONS_ORDER } from "./constants";
import { sortVerifications, verificationNameToTitle } from "./utils";
import { Show } from "shared/components";
import { CancelOrRefundModal } from "shared/app/CancelOrRefundModal";
import { ChangeRoleModal } from "shared/app/ChangeRoleModal";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { combineVerifications } from "shared/utils";
import { TableBody } from "views/applications-id/components/ApplicantsTable/components/TableBody";
import { Attachment02 } from "untitledui-js-base";
import { RefundModal } from "../../../../shared/app/RefundModal";
import { CLEARA_CATEGORIZATION_SCHEMA_VERSION } from "views/applications-id-report/utils/constants";
import { VerificationSchemaNames } from "types/landlord/verificationTemplate";
import { RequiresOverridesIdentityVerification } from "shared/types";

type ApplicantsTableProps = {
  applicants: (Applicant | Invitee)[];
  applicationId?: string;
  verificationSchemas?: VerificationSchemaNames;
  invalidateQueries?: unknown[];
  refunded?: boolean;
  scoreBased?: boolean;
  ruleBased?: boolean;
  applicationStatus?: ApplicationStatus;
  onChangeRole: () => void;
  eligibleForRoleUpdate?: boolean;
  allowCoSigner: boolean;
  requiresOverridesIdentityVerification?: RequiresOverridesIdentityVerification;
};

export const ApplicantsTable = ({
  applicants: _applicants,
  applicationId,
  verificationSchemas,
  invalidateQueries,
  applicationStatus,
  ruleBased,
  onChangeRole,
  eligibleForRoleUpdate,
  scoreBased,
  allowCoSigner,
  requiresOverridesIdentityVerification
}: ApplicantsTableProps) => {
  const queryClient = useQueryClient();

  const [cancelApplicantModal, setCancelApplicantModal] = useState<
    undefined | string
  >();
  const [refundModal, setRefundModal] = useState<undefined | string>();

  const [changeRoleModal, setChangeRoleModal] = useState<
    undefined | FullApplicant
  >();

  const HIDDEN_VERIFICATIONS = [
    ...DISCLOSURES,
    VerificationNames.RentalVerification
  ];

  const { mutate: changeRole } = useMutation({
    mutationKey: ["CHANGE_ROLE", applicationId],
    mutationFn: (role: ApplicantType) =>
      instance
        .post(
          `/portal/v1/applications/${applicationId}/applicants/${changeRoleModal?.id}/change_role`,
          {
            role: role
          }
        )
        .then(() => {
          toast.success("Role updated successfully");
          setChangeRoleModal(undefined);

          if (invalidateQueries)
            queryClient.invalidateQueries({
              queryKey: invalidateQueries
            });

          onChangeRole();
        })
        .catch((err) => {
          toast.error(getErrorMessage(err));
          setChangeRoleModal(undefined);
        })
  });

  const verifications = useMemo(() => {
    if (!verificationSchemas) return [] as VerificationNames[];
    return Object.entries(verificationSchemas)
      .filter(
        ([v, vValue]) =>
          !(
            HIDDEN_VERIFICATIONS.includes(v as VerificationNames) ||
            (v === VerificationNames.HousingHistory &&
              (
                vValue as {
                  address_step_enabled: boolean;
                }
              ).address_step_enabled === false) ||
            (v === VerificationNames.IncomeVerification &&
              (
                vValue as {
                  income_verification_step_enabled: boolean;
                }
              ).income_verification_step_enabled === false)
          )
      )
      .map((v) => v[0]) as VerificationNames[];
  }, [verificationSchemas]);

  // Need to cast since our types are busted
  const applicants = _applicants as unknown[] as Array<ApplicantOrInvitee>;
  if (!applicants.length) return null;

  /*
    we might have issues with applicants/co signers using different vendors for criminal
    we don't support more categories for CIC, and in the long term, we will disable
    so I think we will be fine, just pointed out
  */
  const hasClearaCategorization = applicants.some((applicant) =>
    applicant.verifications?.some(
      (v) =>
        v.name === VerificationNames.CriminalReport &&
        v.vendor === DataVendors.Cleara &&
        v.schema?.version &&
        v.schema.version >= CLEARA_CATEGORIZATION_SCHEMA_VERSION
    )
  );

  return (
    <>
      <Show if={Boolean(cancelApplicantModal)}>
        <CancelOrRefundModal
          onClose={() => {
            setCancelApplicantModal(undefined);
          }}
          onSuccess={() => {
            if (invalidateQueries)
              queryClient.invalidateQueries({
                queryKey: invalidateQueries
              });

            setCancelApplicantModal(undefined);
          }}
          applicationId={String(applicationId)}
          applicantId={String(cancelApplicantModal)}
        />
      </Show>
      <Show if={Boolean(changeRoleModal)}>
        <ChangeRoleModal
          onClose={() => setChangeRoleModal(undefined)}
          onChange={(selectedRole) => {
            changeRole(selectedRole);
          }}
          isOpen={Boolean(changeRoleModal)}
          applicant={changeRoleModal}
        />
      </Show>
      <Show if={Boolean(refundModal)}>
        <RefundModal
          onClose={() => setRefundModal(undefined)}
          applicationId={String(applicationId)}
          applicantId={String(refundModal)}
          queryKey={invalidateQueries}
        />
      </Show>
      <Card
        className="w-full hidden md:grid relative overflow-x-auto max-w-[calc(100vw-128px)] lg:max-w-[calc(100vw-296px)]"
        style={{
          gridTemplateColumns:
            applicants.length > 1
              ? `300px repeat(${applicants.length - 1}, 340px) minmax(340px, 1fr)`
              : `300px minmax(340px, 1fr)`
        }}
      >
        {/* --- HEADER --- */}
        <div className="top-0 sticky left-0 bg-white divide-y divide-alpha-40 frozen-column">
          <Cell className="!h-[138px]" />

          <Cell isFirst isDataCell>
            Started
          </Cell>

          <Cell isFirst isDataCell>
            Submitted
          </Cell>

          <Cell isFirst isDataCell>
            Application fee
          </Cell>

          {combineVerifications(
            sortVerifications(
              verifications.map((name) => ({
                id: name,
                name: name as VerificationNames
              })) as unknown[] as Verification[],
              VERIFICATIONS_ORDER
            ),
            [
              [
                VerificationNames.IncomeVerification,
                VerificationNames.VerifiedIncomeVerification
              ]
            ]
          ).map((v) => {
            if (Array.isArray(v))
              return (
                <Fragment key={v[0].id}>
                  <Cell className="bg-background-900">
                    <WithIcon
                      Icon={VERIFICATION_ICON[v[0].name as VerificationNames]}
                      className="stroke-content-100"
                    >
                      <p className="text-content-300">Income history</p>
                    </WithIcon>
                  </Cell>

                  <Cell isDataCell>
                    <p>
                      Gross income
                      <span className="text-content-100"> (Verified)</span>
                    </p>
                  </Cell>
                  <Cell isDataCell>
                    <p>
                      Net income
                      <span className="text-content-100"> (Verified)</span>
                    </p>
                  </Cell>
                  <Cell isDataCell>
                    <p>
                      Gross income
                      <span className="text-content-100"> (Self-reported)</span>
                    </p>
                  </Cell>
                </Fragment>
              );
            else {
              if (v.name === VerificationNames.RentalApplication)
                return (
                  <Fragment key={v.id}>
                    <Cell className="bg-background-900">
                      <WithIcon
                        Icon={VERIFICATION_ICON[v.name as VerificationNames]}
                        className="stroke-content-100"
                      >
                        <p className="text-content-300">
                          {verificationNameToTitle(v.name as VerificationNames)}
                        </p>
                      </WithIcon>
                    </Cell>

                    <Cell isDataCell>Dependents</Cell>
                    <Cell isDataCell>Vehicles</Cell>
                    <Cell isDataCell>Pets or service animals</Cell>
                  </Fragment>
                );

              if (v.name === VerificationNames.HousingHistory)
                return (
                  <Fragment key={v.id}>
                    <Cell className="bg-background-900">
                      <WithIcon
                        Icon={VERIFICATION_ICON[v.name as VerificationNames]}
                      >
                        {verificationNameToTitle(v.name as VerificationNames)}
                      </WithIcon>
                    </Cell>

                    <Cell isDataCell>Number of residences</Cell>
                    <Cell isDataCell>Average monthly payments</Cell>
                    <Cell isDataCell>Total months of residence</Cell>
                    {verificationSchemas?.rental_verification ? (
                      <Cell isDataCell>Landlord verification</Cell>
                    ) : null}
                  </Fragment>
                );

              if (v.name === VerificationNames.IdentityVerification)
                return (
                  <Fragment key={v.id}>
                    <Cell className="bg-background-900">
                      <WithIcon
                        Icon={VERIFICATION_ICON[v.name as VerificationNames]}
                        className="stroke-content-100"
                      >
                        <p className="text-content-300">
                          {verificationNameToTitle(v.name as VerificationNames)}
                        </p>
                      </WithIcon>
                    </Cell>

                    <Cell isDataCell>Identity verification</Cell>
                  </Fragment>
                );

              if (v.name === VerificationNames.CreditReport)
                return (
                  <Fragment key={v.id}>
                    <Cell className="bg-background-900">
                      <WithIcon
                        Icon={VERIFICATION_ICON[v.name as VerificationNames]}
                      >
                        {verificationNameToTitle(v.name as VerificationNames)}
                      </WithIcon>
                    </Cell>
                    <Cell
                      isDataCell
                      className="flex items-center pl-0 pr-0 relative"
                      isFirst
                    >
                      Credit score
                    </Cell>
                    <Cell
                      isDataCell
                      className="flex items-center pl-0 pr-0 relative"
                    >
                      Past due accounts
                    </Cell>
                    <Cell
                      isDataCell
                      className="flex items-center pl-0 pr-0 relative"
                    >
                      Balance past due
                    </Cell>
                    <Cell
                      isDataCell
                      className="flex items-center pl-0 pr-0 relative"
                    >
                      Balance of collections
                    </Cell>
                    <Cell
                      isDataCell
                      className="flex items-center pl-0 pr-0 relative"
                    >
                      Bankruptcy
                    </Cell>
                  </Fragment>
                );

              if (v.name === VerificationNames.EvictionReport)
                return (
                  <Fragment key={v.id}>
                    <Cell className="bg-background-900">
                      <WithIcon
                        Icon={VERIFICATION_ICON[v.name as VerificationNames]}
                      >
                        {verificationNameToTitle(v.name as VerificationNames)}
                      </WithIcon>
                    </Cell>
                    <Cell
                      isDataCell
                      className="flex items-center pl-0 pr-0 relative"
                      isFirst
                    >
                      Events reported
                    </Cell>
                  </Fragment>
                );

              if (v.name === VerificationNames.CriminalReport)
                return (
                  <Fragment key={v.id}>
                    <Cell className="bg-background-900">
                      <WithIcon
                        Icon={VERIFICATION_ICON[v.name as VerificationNames]}
                      >
                        {verificationNameToTitle(v.name as VerificationNames)}
                      </WithIcon>
                    </Cell>
                    {hasClearaCategorization ? (
                      <>
                        <Cell isDataCell>Felony records</Cell>
                        <Cell isDataCell>Misdemeanor records</Cell>
                        <Cell isDataCell>Other criminal records</Cell>
                      </>
                    ) : (
                      <>
                        <Cell isDataCell>Criminal records</Cell>
                        <Cell isDataCell>Criminal traffic records</Cell>
                      </>
                    )}

                    <Cell isDataCell>National sex offender records </Cell>
                    <Cell isDataCell>Sanction records (e.g. OFAC)</Cell>
                  </Fragment>
                );

              return null;
            }
          })}

          <Cell className="bg-background-900">
            <WithIcon Icon={Attachment02}>Additional documents</WithIcon>
          </Cell>
          <Cell isDataCell>Documents uploaded</Cell>
        </div>
        {/* ---- BODY ---- */}
        <TableBody
          allowCoSigner={allowCoSigner}
          applicants={applicants}
          verifications={verifications}
          applicationStatus={applicationStatus}
          applicationId={applicationId}
          eligibleForRoleUpdate={eligibleForRoleUpdate}
          ruleBased={ruleBased}
          scoreBased={scoreBased}
          hasClearaCategorization={hasClearaCategorization}
          setCancelApplicantModal={setCancelApplicantModal}
          setChangeRoleModal={setChangeRoleModal}
          setRefundModal={setRefundModal}
          verificationSchemas={verificationSchemas}
          requiresOverridesIdentityVerification={
            requiresOverridesIdentityVerification
          }
        />
      </Card>

      <div className="md:hidden flex flex-wrap gap-4">
        {applicants.map((applicant) => {
          const isApplicant = "first_name" in applicant;
          return (
            <Card
              key={applicant.email}
              className="max-w-[280px] w-full"
              linkTo={`/applications/${applicationId}/reports?applicantId=${"id" in applicant ? applicant.id : applicant.email}`}
            >
              <ApplicantHeaderCell
                allowCoSigner={allowCoSigner}
                setCancelApplicantModal={setCancelApplicantModal}
                setChangeRoleModal={setChangeRoleModal}
                setRefundModal={setRefundModal}
                applicant={applicant}
                applicationStatus={applicationStatus}
                applicationId={applicationId}
                eligibleForRoleUpdate={eligibleForRoleUpdate}
              />
              <div className="p-4">
                <p className="text-sm text-content-500">
                  Started at:{" "}
                  <span className="text-xs text-content-500 font-bold">
                    {isApplicant && applicant.application_started_at
                      ? moment(applicant.application_started_at).format("lll")
                      : "-"}
                  </span>
                </p>
                <p className="text-sm text-content-500">
                  Finished at:{" "}
                  <span className="text-xs text-content-500 font-bold">
                    {isApplicant && applicant.application_finished_at
                      ? moment(applicant.application_finished_at).format("lll")
                      : "-"}
                  </span>
                </p>

                <div className="h-px my-3 bg-alpha-100" />

                <div className="divide-y divide-alpha-80">
                  {"verifications" in applicant
                    ? applicant.verifications
                        ?.filter((d) => {
                          return !HIDDEN_VERIFICATIONS.includes(
                            d.name as VerificationNames
                          );
                        })
                        .map((v) => {
                          return (
                            <div key={v.id} className="pt-1 mt-3">
                              <p className="text-sm text-content-600 font-medium mb-2">
                                {verificationNameToTitle(
                                  v.name as VerificationNames
                                ) || v.name}
                              </p>
                              <span className="text-xs text-content-500 font-bold">
                                <VerificationBadge verification={v} />
                              </span>
                            </div>
                          );
                        })
                    : null}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </>
  );
};
